import { FormProvider } from "@gigpro/ui/Form";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { convertToTimezone, formatDate } from "@gigpro/utils/datetime";
import { ApiError } from "@sidegigllc/api";
import type { ConvertibleShift, WeeklyPattern } from "@sidegigllc/api/client";
import { useQuery } from "@tanstack/react-query";
import { pickedShifts } from "domains/hire/components/ConvertibleShiftList";
import { conversionsOf } from "domains/hire/components/ConvertibleShiftList/seats";
import { PostFreeChoiceCard } from "domains/hire/components/PostFreeChoiceCard";
import {
  WorkingInterviewConvertStep,
  WorkingInterviewStep,
  type WorkingInterviewChoice,
  WorkingInterviewStepLoading,
  type WorkingInterviewStepProps,
} from "domains/hire/components/WorkingInterviewStep";
import { convertSummary } from "domains/hire/components/WorkingInterviewStep/convert";
import {
  seriesDefaults,
  seriesSchemaFor,
  seriesSummary,
  toPreviewRequest,
  toSeriesRequest,
} from "domains/hire/components/WorkingInterviewStep/series";
import { useJobsGateState } from "domains/hire/hooks/useJobsGateState";
import { type JobsGate, askAnAdmin, jobsTeaserCopy, postFreeChoiceCopy } from "domains/hire/jobsPlan";
import React from "react";
import { useWatch } from "react-hook-form";
import { convertibleShiftsQuery, locationQuery, seriesPreviewQuery, weeklyPatternQuery } from "./queries";

interface WorkingInterviewStepContainerProps extends Pick<WorkingInterviewStepProps, "onBack" | "onSkip"> {
  locationId: number;
  /** The job's role; the pattern is read from its history. */
  skillId: number | undefined;
  /** A posted shift that starts as the only one picked, when it's among the role's convertible shifts. */
  preselectedGigId?: number;
  /** Whether the business is on the step. The dialog keeps it mounted throughout, so nothing is read before it's reached. */
  active: boolean;
  onContinue: (choice: WorkingInterviewChoice) => void;
  /** What stands between the location and working interviews (decision 4); null once it has full access. */
  gate?: JobsGate | null;
  /** Opens the trial or Resubscribe step from the choice card; the card stays if it fails or is cancelled. */
  onStartPlan: () => void;
}

const formatDay = (date: Date) => formatDate(date, "MMM d");

interface StepBodyProps extends WorkingInterviewStepContainerProps {
  /** Location zone the schedule is read and sent in. */
  timezone: string;
  pattern: WeeklyPattern | null;
  onUseMyShifts?: () => void;
}

const StepBody = ({
  locationId,
  active,
  timezone,
  pattern,
  onBack,
  onSkip,
  onContinue,
  onUseMyShifts,
}: StepBodyProps) => {
  const schema = React.useMemo(() => seriesSchemaFor(timezone), [timezone]);
  // Mounted once the pattern is back, so its defaults are the pre-fill without a reset.
  const form = useZodForm({ schema, defaultValues: seriesDefaults(pattern, timezone) });

  const values = useWatch({ control: form.control });
  const parsed = schema.safeParse(values);
  const previewRequest = parsed.success ? toPreviewRequest(parsed.data, timezone) : undefined;
  // No placeholder: the last edit's count would read as this one's and Continue would carry it to Review.
  const previewResult = useQuery(seriesPreviewQuery({ locationId, request: previewRequest, enabled: active }));
  const preview = {
    // `until` is an instant: shown as the location's calendar day, which the browser's can differ from.
    data:
      previewRequest && previewResult.data
        ? { ...previewResult.data, until: convertToTimezone(previewResult.data.until, timezone) }
        : undefined,
    isLoading: previewResult.isFetching,
    error: previewResult.error ? ApiError.fromThrown(previewResult.error).getMainErrorMessage() : undefined,
  };

  const handleContinue = async () => {
    // No server count means the business hasn't seen what it gets (decision 4); a refused preview refuses create too.
    if (!(await form.trigger()) || !preview.data) return;
    const series = form.getValues();
    onContinue({
      kind: "series",
      request: toSeriesRequest(series, timezone),
      summary: seriesSummary(series, preview.data, formatDay),
    });
  };

  return (
    <FormProvider {...form}>
      <WorkingInterviewStep
        patternSampleSize={pattern?.sample_size ?? 0}
        preview={preview}
        onBack={onBack}
        onSkip={onSkip}
        onContinue={handleContinue}
        onUseMyShifts={onUseMyShifts}
      />
    </FormProvider>
  );
};

interface ConvertBodyProps extends Pick<StepBodyProps, "timezone" | "onBack" | "onSkip" | "onContinue"> {
  shifts: Array<ConvertibleShift>;
  selected: Array<string>;
  onSelectedChange: (selected: Array<string>) => void;
  seats: Record<number, number>;
  onSeatsChange: (gigId: number, seats: number) => void;
  onScheduleNew: () => void;
}

const ConvertBody = ({
  shifts,
  selected,
  onSelectedChange,
  seats,
  onSeatsChange,
  timezone,
  onBack,
  onSkip,
  onContinue,
  onScheduleNew,
}: ConvertBodyProps) => {
  const picked = pickedShifts(shifts, selected);
  const handleContinue = () =>
    onContinue({
      kind: "convert",
      conversions: conversionsOf(picked, seats),
      summary: convertSummary(picked, timezone),
    });

  return (
    <WorkingInterviewConvertStep
      shifts={shifts}
      timezone={timezone}
      selected={selected}
      onSelectedChange={onSelectedChange}
      seats={seats}
      onSeatsChange={onSeatsChange}
      onBack={onBack}
      onSkip={onSkip}
      onContinue={picked.length > 0 ? handleContinue : undefined}
      onScheduleNew={onScheduleNew}
    />
  );
};

const allPicked = (shifts: Array<ConvertibleShift>) => shifts.map((shift) => String(shift.gig_id));
// A shift brought here to convert starts alone (or nothing is picked if it isn't listed); otherwise all start picked.
const initialPicks = (shifts: Array<ConvertibleShift>, preselectedGigId: number | undefined) => {
  if (preselectedGigId == null) return allPicked(shifts);
  return shifts.some((shift) => shift.gig_id === preselectedGigId) ? [String(preselectedGigId)] : [];
};

const StepBranches = ({
  shifts,
  preselectedGigId,
  ...props
}: StepBodyProps & { shifts: Array<ConvertibleShift>; preselectedGigId?: number }) => {
  // Decided once from the first settled list; after that only the business's own switch changes it, so a refetch
  // that finds shifts never swaps out a schedule being edited.
  const [branch, setBranch] = React.useState<"convert" | "series">(() => (shifts.length > 0 ? "convert" : "series"));
  // Held here so a trip to the schedule and back keeps the picks; every shift starts picked, as on the job page.
  // Unset until the list first has shifts, so switching over to shifts that arrived later still starts all picked.
  const [selected, setSelected] = React.useState<Array<string> | null>(() =>
    shifts.length > 0 ? initialPicks(shifts, preselectedGigId) : null,
  );
  // Held with the picks, so a trip to the schedule and back keeps what was chosen too.
  const [seats, setSeats] = React.useState<Record<number, number>>({});
  // A list that empties on refetch leaves only the schedule.
  if (branch === "convert" && shifts.length > 0)
    return (
      <ConvertBody
        {...props}
        shifts={shifts}
        selected={selected ?? []}
        onSelectedChange={setSelected}
        seats={seats}
        onSeatsChange={(gigId, next) => setSeats((previous) => ({ ...previous, [gigId]: next }))}
        onScheduleNew={() => setBranch("series")}
      />
    );
  const switchToShifts = () => {
    setSelected((current) => current ?? initialPicks(shifts, preselectedGigId));
    setBranch("convert");
  };
  return <StepBody {...props} onUseMyShifts={shifts.length > 0 ? switchToShifts : undefined} />;
};

interface GatedStepProps extends Pick<
  WorkingInterviewStepContainerProps,
  "locationId" | "onBack" | "onSkip" | "onStartPlan"
> {
  gate: JobsGate;
}

/** Posting is free (decision 1), so a location without a plan is offered the job first and the plan second. */
const GatedStep = ({ gate, locationId, onBack, onSkip, onStartPlan }: GatedStepProps) => {
  const { location, status } = useJobsGateState(locationId);
  const { planLine, planActionLabel } = postFreeChoiceCopy(gate, !!status?.can_start_trial);
  const teaser = jobsTeaserCopy(status, { locationName: location?.name ?? "", trialEndsAt: new Date() });
  const adminNote =
    gate === "askAdmin" && teaser ? askAnAdmin(teaser, location?.name ?? "your location").body : undefined;
  return (
    <PostFreeChoiceCard
      planLine={planLine}
      planActionLabel={planActionLabel}
      adminNote={adminNote}
      onPost={onSkip}
      onStartPlan={onStartPlan}
      onBack={onBack}
    />
  );
};

/** The posting step's data: the location's timezone and the suggested pattern behind the pre-filled schedule. */
export const WorkingInterviewStepContainer = (props: WorkingInterviewStepContainerProps) => {
  const { locationId, skillId, active, gate, onBack, onSkip, onStartPlan } = props;
  const location = useQuery(locationQuery({ locationId }));
  // The card replaces the schedule, so nothing is read for it until the plan gives the location full access.
  const reads = active && !gate;
  const pattern = useQuery(weeklyPatternQuery({ locationId, skillId, enabled: reads }));
  const convertible = useQuery(convertibleShiftsQuery({ locationId, skillId, enabled: reads }));
  const timezone = location.data?.timezone;

  if (gate)
    return <GatedStep gate={gate} locationId={locationId} onBack={onBack} onSkip={onSkip} onStartPlan={onStartPlan} />;

  // Without the location's timezone the schedule can't be read right, so the only way on is Skip.
  if (location.isError || (location.data && !timezone)) {
    return (
      <WorkingInterviewStepLoading
        onBack={onBack}
        onSkip={onSkip}
        error="We couldn't load your location's schedule. Skip this step to post the job without working interviews."
      />
    );
  }
  // A failed pattern or list read falls back to the blank picker or the schedule rather than blocking the post.
  // Pending, not loading: before the step is reached the queries are off, and the step must wait for both.
  if ((skillId != null && (pattern.isPending || convertible.isPending)) || !timezone)
    return <WorkingInterviewStepLoading onBack={onBack} onSkip={onSkip} />;

  return (
    <StepBranches
      // A new role means a new suggestion and new shifts, so start the step over.
      key={skillId}
      {...props}
      timezone={timezone}
      pattern={pattern.data?.pattern ?? null}
      shifts={convertible.data?.results ?? []}
    />
  );
};
